class ProductCylindo extends HTMLElement {
	constructor() {
		super()

		this.cylindoConfig = {
			productCode: 		this.dataset.cylindoProductCode,
			accountID: 			5055,
			country: 			'us',
			maxZoom: 			'mixed',
			rotationSpeed: 		2,
			thumbs: 			false,
			tooltipDragText:	'Drag to Rotate. Click to Zoom',
			frames: 			[ 1 ]
		}

		this.cylindoProductData 	= JSON.parse( this.dataset.cylindoProductData )
		this.cylindoVirtualFeatures	= JSON.parse( this.dataset.cylindoVirtualFeatures )
		this.cylindoActiveVariantId	= null

		this.setActiveVariantId()

		// to be populated later
		this.cylindoVariantData = []

		// to be populated when Cylindo is executed
		this.cylindoInstances = []

		// to be populated later
		this.cylindoVirtualFeatureCombinations = []

		this.cylindoNodes = {
			container: 		this.querySelector('.cylindo-container'),
			navArrows: 		this.querySelector('.cylindo-arrow-nav-container'),
			viewerNavBtns:	this.querySelector('.cylindo-nav'),
			viewerParent:	this.querySelector('.cylindo-viewers-parent'),
			viewers: 		[],
		}

		this.bind()
	}


	/**
	 * Add event listeners
	 *
	 * @return {void}
	 */
	bind() {
		document.addEventListener( 'rf:product:variantChange', e => {

			// added 2023-06-23
			const id = e?.detail?.activeVariant?.id

			if( id ) {
				this.setActiveVariantId( id )
			}

			this.handleVariationChange( e )
		})
	}


	/**
	 * Set active variant ID. If no value provided, pull from query string
	 *
	 * @param	{int}	id
	 * @return 	{self} 	for chaining
	 */
	setActiveVariantId( id = null ) {
		if( !id ) {
			const urlParams = new URLSearchParams( window.location.search )

			if( urlParams.has( 'variant' ) ) {
				id = parseInt( urlParams.get( 'variant' ) )
			}
		}

		if( id && !isNaN( id ) ) {
			this.cylindoActiveVariantId = id
		}

		return this
	}


	/**
	 * Set variant data.
	 * 
	 * We're doing this versus pulling from dataset because of limitations in Shopify's Liquid functionality.
	 *
	 * @param	{string|obj}	data
	 * @return	{self} 			for chaining
	 */
	setVariantData( data ) {
		if( 'string' === typeof data ) {
			try {
				data = JSON.parse( data )
				data = data.map( variant => {
					variant.features = JSON.parse( variant.features )
					return variant
				})

			} catch ( err ) {
				console.warn( 'Failed to parse product variant data.' )
			}
		}

		this.cylindoVariantData = data

		return this
	}


	/**
	 * Execute Cylindo functionality
	 *
	 * @param 	{array}	variantFeatures	Features to display specific Cylindo model
	 * @return	{self}
	 */
	runCylindo( variantFeatures = [] ) {
		const self = this
		const cylindo = window.cylindo

		// confirm Cylindo is loaded/exists
		// todo -- set interval for check
		if( !cylindo ) {
			return
		}

		self
			.showLoader()
			.resetViewerSlick()
			.resetNavSlick()

		const opts = this.cylindoConfig
		const combinations = this.parseVirtualFeatures()
				
		// if no features passed, check for active variant to be safe
		if( !variantFeatures.length && this.cylindoActiveVariantId ) {
			const activeVariant = this.cylindoVariantData.find( data => this.cylindoActiveVariantId === data.id )

			if( activeVariant ) {
				try {
					variantFeatures = JSON.parse( activeVariant.features )
				} catch ( err ) {
					console.warn( `Failed to parse variant features for variant #${activeVariant.id}` )
				}
			}
		}

		// clear out preexisting Cylindo objects
		self.cylindoInstances = []

		cylindo.on( 'ready', () => {

			const cylindoPromises = []

			// create Cylindo viewer for each combination of features
			combinations.forEach( ( combination, i ) => {

				// create container node for viewer
				const nodeId = `cylindo-viewer-node-${i}`
				const viewerNode = document.createElement( 'div' )

				viewerNode.setAttribute( 'id', nodeId )
				viewerNode.classList.add( 'cylindo-viewer' )

				self.cylindoNodes.viewerParent.appendChild( viewerNode )
				self.cylindoNodes.viewers.push( viewerNode )

				// match new node with Cylindo options
				opts.containerID = nodeId

				// update options with combination features (including variant features)
				opts.features = [ combination, variantFeatures ].flat()

				cylindoPromises.push(
					new Promise( resolve => {
						const instance = cylindo.viewer.create( opts )

						instance.on( 'instance:viewer:ready', () => {
							self.cylindoInstances.push( instance )
							resolve()
						})
					})
				)
			})

			Promise.all( cylindoPromises ).then( () => {
				self.handleCylindoLoaded()
			})
		})

		return this
	}


	/**
	 * Create array of virtual features (e.g. every single possible combination of features)
	 *
	 * @return	{array}
	 */
	parseVirtualFeatures() {
		const featureSets = []

		this.cylindoVirtualFeatures.map( ( feature, i ) => {
			const { featureCode } 	= feature
			const options			= feature.options.map( option => option.code )

			options.forEach( option => {
				featureSets.push({
					featureCode,
					option
				})
			})
		})

		const combinations = this
			.createFeatureCombinations( featureSets )
			.map( combination => {
				const flattenedValues = []

				combination.forEach( feature => {
					flattenedValues.push( Object.values( feature ) )
				})

				return flattenedValues.flat()
			})

		return combinations
	}

	
	/**
	 * Create combinations of all provided feature sets
	 *
	 * @param	{array} 	features 	Array of cleaned features
	 * @return 	{array} 	
	 */
	createFeatureCombinations( features ) {
		const groups = {}
	  
		features.forEach( ({ featureCode, option }) => {
			if( !groups[ featureCode] ) {
				groups[ featureCode ] = []
		  	}

		  	groups[ featureCode ].push({ featureCode, option })
		})
	  
		let output = [ [] ]

		Object.values( groups ).forEach( group => {
			const temp = []

			group.forEach( item => {
				output.forEach( list => temp.push( item ? list.concat( item ) : list ) )
		  	})

			// deep copy (todo -- not the best, performance-wise)
		  	output = JSON.parse( JSON.stringify( temp ) )
		})

		return output
	}


	/**
	 * Post-Cylindo load actions
	 *
	 * @return	{self}
	 */
	handleCylindoLoaded() {
		this
			.generateCylindoNav()
			.applySlickToViewers()
			.applySlickToNav()
			.hideLoader()

		// added 2023-07-13 :: fix issue with product gallery being hidden on shorter screens
		const container = document.querySelector('.box__product-gallery[data-style=slider]')

		if ( container ) {
			container.style.height = 'unset'
		}

		return this
	}


	/**
	 * Apply slider to viewers
	 *
	 * @return 	self
	 */
	applySlickToViewers() {
		const $ = jQuery

		$(this.cylindoNodes.viewerParent).slick({
			asNavFor:  		this.cylindoNodes.viewerNavBtns,
			draggable:		false,
			fade: 			true,
			focusOnSelect: 	true,
			nextArrow:		$('.cylindo-next'),
			prevArrow:		$('.cylindo-prev'),
			swipe: 			false,
		})

		return this
	}


	/**
	 * Remove slider for viewers and remove viewer elements
	 *
	 * @return 	self
	 */
	resetViewerSlick() {
		const viewers = this.cylindoNodes.viewerParent

		if( viewers.slick ) {
			viewers.slick.unslick()
		}

		viewers.innerHTML = ''

		return this
	}


	/**
	 * Apply slider to nav buttons
	 *
	 * @return 	self
	 */
	applySlickToNav() {
		jQuery(this.cylindoNodes.viewerNavBtns).slick({
			asNavFor:		this.cylindoNodes.viewerParent,
			focusOnSelect: 	true,
			slidesToScroll:	1,
			slidesToShow: 	4,
		})

		return this
	}
	

	/**
	 * Remove slider for nav buttons and remove thumbnails
	 *
	 * @return 	self
	 */
	resetNavSlick() {
		const btns = this.cylindoNodes.viewerNavBtns

		if( btns.slick ) {
			btns.slick.unslick()
		}

		btns.innerHTML = ''

		return this
	}	


	/**
	 * Show loading animation on top of viewer
	 *
	 * @return {self} 	for chaining
	 */
	showLoader() {
		this.cylindoNodes.container.classList.add( 'is-loading' )

		return this
	}


	/**
	 * Hide loading animation on top of viewer
	 *
	 * @return {self} 	for chaining
	 */
	hideLoader() {
		this.cylindoNodes.container.classList.remove( 'is-loading' )
		
		return this
	}


	/**
	 * Generate navigation for current Cylindo models
	 *
	 * @return 	{self}
	 */
	generateCylindoNav() {
		this.cylindoNodes.viewerParent.querySelectorAll('.cylindo-viewer').forEach( node => {
			const img = node.querySelector('.cylindo-viewer-container .cylindo-threesixty-list li img')

			if( img ) {
				const nodeThumb = document.createElement('div')
				const nodeImg 	= document.createElement('img')

				nodeThumb.classList.add( 'cylindo-nav-thumb' )
				nodeImg.classList.add( 'cylindo-nav-thumb__img' )
				nodeImg.setAttribute( 'src', img.getAttribute( 'src' ) )

				nodeThumb.appendChild( nodeImg )
				
				this.cylindoNodes.viewerNavBtns.appendChild( nodeThumb )
			}
		})
		
		return this
	}


	/**
	 * Listen for variation changes and optionally change active Cylindo instance
	 *
	 * @param 	{CustomEvent}	e
	 * @return	{void}
	 */
	handleVariationChange( e ) {
		const options 		= e?.detail?.activeVariantOptions

		let features = []

		if( !options || !Array.isArray( options ) ) {
			return
		}

		const match = this.cylindoVariantData.find( variant => {
			return this.optionsAreEqual( options, variant.title )
		})

		if( !match ) {
			console.warn( `No match found for "${options.join( ' / ' )}"` )
			return
		}

		console.info( `Found match for "${options.join( ' / ' )}"` )

		// values to pass to Cylindo to display correct model
		try { 
			features = JSON.parse( match.features )

		} catch ( err ) {
			console.warn( `Failed to parse variant features: "${match.features}` )
		}

		if( features.length ) {
			this.runCylindo( features )
		}
	}


	/**
	 * Utility function to compare two sets of options
	 * 
	 * @see 	https://stackoverflow.com/a/19746771
	 *
	 * @param 	{string|array} 	target 	Target options
	 * @param 	{string|array} 	compare	Options for comparision
	 * 
	 * @return 	{bool} 			True, if equal
	 */
	optionsAreEqual( target, compare ) {
		target	= this.simplifyOptionsToArray( target )
		compare	= this.simplifyOptionsToArray( compare )

		return target.length === compare.length && target.every( ( value, i ) => {
			return value === compare[ i ]
		})
	}


	/**
	 * Utility function to change options string|array to sorted, "plain" array
	 * 
	 * To make values plain, we're going to do the following to individual values:
	 * 	1. remove white space
	 * 	2. make fully upper case
	 * 	3. alphabetically sort array
	 *
	 * @param 	{string|array} 	input
	 * @param 	{string} 		separator
	 * @return 	{array}
	 */
	simplifyOptionsToArray( input, separator = '/' ) {
		if( 'string' === typeof input ) {
			input = input.split( separator )
		}

		if( !Array.isArray( input ) ) {
			return []
		}

		return input.map( value => value.trim().toUpperCase() ).sort()
	}

}



customElements.define( 'product-cylindo', ProductCylindo )